import React, { useRef, useState, useEffect, createContext, useContext } from 'react';

// Contexte pour partager l'état du scroll horizontal et du drag (copié de app.admin)
const ScrollContext = createContext({ hasHorizontalScroll: false, hasMoved: false });

export const useScrollContext = () => useContext(ScrollContext);

/**
 * Wrapper pour permettre le drag horizontal avec la souris sur mobile/tablette
 * @param {React.ReactNode} children - Contenu de la table à wrapper
 * @param {string} className - Classes CSS additionnelles
 */
const ScrollableTableWrapper = ({ children, className = '' }) => {
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);

  // Détecter si le scroll horizontal est nécessaire
  useEffect(() => {
    const checkScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const hasScroll = container.scrollWidth > container.clientWidth;
        setHasHorizontalScroll(hasScroll);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);

    const resizeObserver = new ResizeObserver(checkScroll);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkScroll);
      resizeObserver.disconnect();
    };
  }, [children]);

  const handleMouseDown = (e) => {
    const container = scrollContainerRef.current;
    if (container) {
      setIsDragging(true);
      setHasMoved(false);
      setStartX(e.pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const container = scrollContainerRef.current;
    if (container) {
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      if (Math.abs(walk) > 5) {
        setHasMoved(true);
      }
      container.scrollLeft = scrollLeft - walk;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    const container = scrollContainerRef.current;
    if (container) {
      container.style.cursor = 'grab';
      container.style.userSelect = '';
    }
    setTimeout(() => {
      setHasMoved(false);
    }, 200);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    const container = scrollContainerRef.current;
    if (container) {
      container.style.cursor = 'grab';
      container.style.userSelect = '';
    }
  };

  return (
    <ScrollContext.Provider value={{ hasHorizontalScroll, hasMoved }}>
      <div
        ref={scrollContainerRef}
        className={`scrollable-table-wrapper overflow-x-auto cursor-grab active:cursor-grabbing w-full ${className}`}
        style={{ 
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', /* Firefox */
          msOverflowStyle: 'none', /* IE and Edge */
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <style>{`
          .scrollable-table-wrapper::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
        `}</style>
        {children}
      </div>
    </ScrollContext.Provider>
  );
};

export default ScrollableTableWrapper;


